import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';

const restorationPhases = [
  {
    phase: 'Phase 1',
    title: 'Assessment & Planning',
    icon: 'ri-search-eye-fill',
    desc: 'Comprehensive damage assessment using thermal imaging and moisture mapping. We document everything and create a detailed restoration plan.',
    items: ['Thermal imaging scan', 'Moisture mapping', 'Structural evaluation', 'Insurance documentation'],
  },
  {
    phase: 'Phase 2',
    title: 'Mitigation & Drying',
    icon: 'ri-temp-cold-fill',
    desc: 'Industrial-grade drying equipment is deployed to remove all moisture from structural materials, preventing mold and secondary damage.',
    items: ['Industrial air movers', 'Dehumidification', 'Structural drying', 'Moisture monitoring'],
  },
  {
    phase: 'Phase 3',
    title: 'Cleaning & Sanitizing',
    icon: 'ri-sparkling-fill',
    desc: 'Deep cleaning and antimicrobial treatment of all affected surfaces to eliminate contaminants and prevent mold growth.',
    items: ['Antimicrobial treatment', 'Odor elimination', 'Content cleaning', 'Air scrubbing'],
  },
  {
    phase: 'Phase 4',
    title: 'Full Restoration',
    icon: 'ri-home-fill',
    desc: 'Complete structural and cosmetic restoration — from drywall and flooring to painting and finishing — returning your property to pre-loss condition.',
    items: ['Drywall replacement', 'Flooring installation', 'Painting & finishing', 'Final inspection'],
  },
];

const beforeAfter = [
  {
    label: 'Water Damage Restoration',
    before: 'https://public.readdy.ai/ai/img_res/62c384079a681a2c4707200a0fd25923.jpg',
    after: 'https://readdy.ai/api/search-image?query=beautifully%20restored%20modern%20room%20with%20fresh%20paint%2C%20new%20flooring%2C%20clean%20walls%2C%20bright%20lighting%2C%20after%20restoration%20photo&width=600&height=400&seq=ba002&orientation=landscape',
  },
  {
    label: 'Fire Damage Restoration',
    before: 'https://readdy.ai/api/search-image?query=fire%20damaged%20room%20with%20charred%20walls%2C%20smoke%20stains%2C%20burned%20furniture%2C%20before%20restoration%20photo&width=600&height=400&seq=ba003&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=fully%20restored%20bright%20modern%20living%20room%20with%20fresh%20paint%2C%20new%20furniture%2C%20clean%20interior%2C%20after%20fire%20restoration&width=600&height=400&seq=ba004&orientation=landscape',
  },
];

const restorationSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Full Property Restoration Services Houston TX',
    description: 'Complete structural and cosmetic property restoration from pre-loss condition. 4-phase process: assessment, drying, cleaning, and full restoration. Serving Houston TX.',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Mitigation Restoration Services',
      telephone: '+18005551234',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Houston',
        addressRegion: 'TX',
        addressCountry: 'US',
      },
    },
    areaServed: { '@type': 'City', name: 'Houston' },
    serviceType: 'Property Restoration',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/property-restoration`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '5000',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services` },
      { '@type': 'ListItem', position: 3, name: 'Property Restoration', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/property-restoration` },
    ],
  },
];

export default function PropertyRestorationPage() {
  return (
    <div className="min-h-screen">
      <SeoHead
        title="Property Restoration Services Houston TX | Mitigation Restoration"
        description="Full property restoration in Houston TX — from water & fire damage to complete structural rebuilds. 4-phase certified process, 5,000+ properties restored, 98% satisfaction rate."
        keywords="property restoration Houston TX, water damage restoration, fire damage restoration, structural restoration, drywall repair Houston"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/property-restoration`}
        schema={restorationSchema}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20property%20restoration%20team%20working%20on%20interior%20renovation%20after%20water%20damage%2C%20skilled%20workers%20with%20tools%2C%20bright%20clean%20workspace&width=1920&height=800&seq=prhero001&orientation=landscape"
            alt="Property Restoration"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1f35]/90 via-[#0d1f35]/70 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-32 pb-20">
          <span className="inline-flex items-center gap-2 bg-[#DC2626] text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-widest mb-5">
            <i className="ri-tools-fill"></i>
            Full Property Restoration
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Restore Your Property<br />to Pre-Loss Condition
          </h1>
          <p className="text-white/80 text-base max-w-lg mb-8">
            Our certified restoration specialists handle every phase of the recovery process — from initial mitigation to the final coat of paint — with precision and care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="tel:+18005551234"
              className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-8 py-4 rounded-full font-semibold text-sm hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              Get Free Assessment
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-sm hover:bg-white hover:text-[#1E3A5F] transition-colors cursor-pointer whitespace-nowrap"
            >
              Contact Us <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Restoration Phases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <span className="text-[#DC2626] text-xs font-semibold uppercase tracking-widest">Our Approach</span>
            <h2 className="text-3xl font-extrabold text-[#1E3A5F] mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              The 4-Phase Restoration Process
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {restorationPhases.map((phase, idx) => (
              <div key={phase.phase} className="relative">
                {idx < restorationPhases.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-gray-200 z-0" style={{ width: 'calc(100% - 2rem)' }}></div>
                )}
                <div className="bg-[#F8F9FA] rounded-2xl p-6 relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#DC2626] shrink-0">
                      <i className={`${phase.icon} text-white text-sm`}></i>
                    </div>
                    <span className="text-xs font-semibold text-[#DC2626] uppercase tracking-widest">{phase.phase}</span>
                  </div>
                  <h3 className="font-bold text-[#1E3A5F] text-base mb-2">{phase.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">{phase.desc}</p>
                  <ul className="space-y-1.5">
                    {phase.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-check-line text-[#DC2626]"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before & After */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-[#DC2626] text-xs font-semibold uppercase tracking-widest">Results</span>
            <h2 className="text-3xl font-extrabold text-[#1E3A5F] mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Before & After Transformations
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {beforeAfter.map((item) => (
              <div key={item.label} className="bg-white rounded-2xl overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="relative">
                    <img src={item.before} alt="Before" className="w-full h-56 object-cover object-top" />
                    <span className="absolute top-3 left-3 bg-gray-800 text-white text-xs font-semibold px-3 py-1 rounded-full">Before</span>
                  </div>
                  <div className="relative">
                    <img src={item.after} alt="After" className="w-full h-56 object-cover object-top" />
                    <span className="absolute top-3 left-3 bg-[#DC2626] text-white text-xs font-semibold px-3 py-1 rounded-full">After</span>
                  </div>
                </div>
                <div className="p-4 text-center">
                  <p className="font-semibold text-[#1E3A5F] text-sm">{item.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#1E3A5F]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '5,000+', label: 'Properties Restored' },
              { value: '98%', label: 'Client Satisfaction' },
              { value: '15+ yrs', label: 'Industry Experience' },
              { value: '100%', label: 'Insurance Accepted' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-4xl font-extrabold text-white mb-1">{stat.value}</p>
                <p className="text-white/60 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
